package com.patronat.top.client.presentation.auth

import com.patronat.top.core.base.BaseViewModel
import com.patronat.top.core.domain.usecase.command.auth.ConfirmPhoneCommand
import com.patronat.top.core.domain.usecase.command.auth.LoginCommand
import com.patronat.top.core.domain.usecase.command.auth.RegistrationUserCommand
import com.patronat.top.core.domain.usecase.command.auth.SendCodeToPhoneCommand
import com.patronat.top.core.presentation.AlertController
import com.patronat.top.core.presentation.NotificationType
import com.patronat.top.core.utils.UiText
import com.patronat.top.core.validation.Validator
import com.patronat.top.dto.AuthFlow
import com.patronat.top.dto.AuthMethod
import com.patronat.top.dto.UserRole
import com.patronat.top.features.auth.domain.models.AuthResult
import com.patronat.top.features.auth.domain.usecase.ClientConfirmPhoneUseCase
import com.patronat.top.features.auth.domain.usecase.ResendPhoneCodeUseCase
import com.patronat.top.features.auth.domain.usecase.StartLoginUseCase
import com.patronat.top.features.auth.domain.usecase.StartRegistrationUseCase
import top.clientapp.generated.resources.Res
import top.clientapp.generated.resources.auth_error_check_data
import top.clientapp.generated.resources.auth_error_enter_first_name
import top.clientapp.generated.resources.auth_error_enter_last_name
import top.clientapp.generated.resources.auth_error_enter_phone
import top.clientapp.generated.resources.auth_error_invalid_code
import top.clientapp.generated.resources.auth_error_invalid_email
import top.clientapp.generated.resources.auth_error_invalid_phone
import top.clientapp.generated.resources.auth_error_login_failed
import top.clientapp.generated.resources.auth_error_registration_failed
import top.clientapp.generated.resources.auth_error_resend_failed
import top.design_system.generated.resources.Res as DesignRes
import top.design_system.generated.resources.error_alert_title

class ClientAuthViewModel(
    private val alertController: AlertController,
    private val startLoginUseCase: StartLoginUseCase,
    private val startRegistrationUseCase: StartRegistrationUseCase,
    private val confirmPhoneUseCase: ClientConfirmPhoneUseCase,
    private val resendPhoneCodeUseCase: ResendPhoneCodeUseCase
) : BaseViewModel<ClientAuthViewState, ClientAuthEvent, ClientAuthEffect>(
    initialState = ClientAuthViewState()
) {
    override fun onEvent(event: ClientAuthEvent) {
        when (event) {
            ClientAuthEvent.OnRegisterByPhoneClicked -> {
                updateState { it.copy(step = ClientAuthStep.RegistrationForm, flow = AuthFlow.REGISTRATION).withoutErrors() }
            }

            ClientAuthEvent.OnLoginByPhoneClicked -> {
                updateState { it.copy(step = ClientAuthStep.LoginForm, flow = AuthFlow.LOGIN).withoutErrors() }
            }

            ClientAuthEvent.OnContinueWithoutAccountClicked -> sendEffect(ClientAuthEffect.ContinueWithoutAccount)
            ClientAuthEvent.OnBackClicked -> handleBack()
            is ClientAuthEvent.OnFirstNameChanged -> updateState {
                it.copy(firstName = event.value, firstNameError = null)
            }

            is ClientAuthEvent.OnLastNameChanged -> updateState {
                it.copy(lastName = event.value, lastNameError = null)
            }

            is ClientAuthEvent.OnPhoneChanged -> updateState {
                it.copy(phone = event.value, phoneError = null)
            }

            is ClientAuthEvent.OnEmailChanged -> updateState {
                it.copy(email = event.value, emailError = null)
            }

            ClientAuthEvent.OnSubmitRegistration -> submitRegistration()
            ClientAuthEvent.OnSubmitLogin -> submitLogin()
            is ClientAuthEvent.OnSubmitPhoneCode -> submitPhoneCode(event.code)
            ClientAuthEvent.OnResendPhoneCode -> resendPhoneCode()
            ClientAuthEvent.OnSuccessPrimaryClicked -> {
                updateState { it.copy(showSuccessSheet = false) }
                sendEffect(ClientAuthEffect.NavigateToHome(UserRole.CLIENT))
            }
            ClientAuthEvent.OnDismissSuccess -> updateState { it.copy(showSuccessSheet = false) }
        }
    }

    private fun submitRegistration() {
        val firstNameError = if (!Validator.isValidName(currentState.firstName)) {
            UiText.StringResource(Res.string.auth_error_enter_first_name)
        } else {
            null
        }

        val lastNameError = if (!Validator.isValidName(currentState.lastName)) {
            UiText.StringResource(Res.string.auth_error_enter_last_name)
        } else {
            null
        }

        val phoneError = when {
            currentState.phone.isBlank() -> UiText.StringResource(Res.string.auth_error_enter_phone)
            !Validator.isValidPhone(currentState.phone) -> UiText.StringResource(Res.string.auth_error_invalid_phone)
            else -> null
        }

        val emailError = when {
            currentState.email.isNotBlank() && !Validator.isValidEmail(currentState.email) ->
                UiText.StringResource(Res.string.auth_error_invalid_email)

            else -> null
        }

        if (firstNameError != null || lastNameError != null || phoneError != null || emailError != null) {
            updateState {
                it.copy(
                    firstNameError = firstNameError,
                    lastNameError = lastNameError,
                    phoneError = phoneError,
                    emailError = emailError
                )
            }
            showError(UiText.StringResource(Res.string.auth_error_check_data))
            return
        }

        launchNetwork(
            request = {
                startRegistrationUseCase.execute(
                    RegistrationUserCommand(
                        firstName = currentState.firstName.trim(),
                        lastName = currentState.lastName.trim(),
                        phone = currentState.phone,
                        email = currentState.email.trim().ifBlank { null },
                        role = UserRole.CLIENT
                    )
                )
            },
            onLoading = { loading -> updateState { it.copy(isLoading = loading) } },
            onError = { _, message ->
                showError(message?.let(UiText::DynamicString) ?: UiText.StringResource(Res.string.auth_error_registration_failed))
            },
            onSuccess = ::handleAuthResult
        )
    }

    private fun submitLogin() {
        val phoneError = when {
            currentState.phone.isBlank() -> UiText.StringResource(Res.string.auth_error_enter_phone)
            !Validator.isValidPhone(currentState.phone) -> UiText.StringResource(Res.string.auth_error_invalid_phone)
            else -> null
        }

        if (phoneError != null) {
            updateState { it.copy(phoneError = phoneError) }
            showError(phoneError)
            return
        }

        launchNetwork(
            request = {
                startLoginUseCase.execute(
                    LoginCommand(
                        login = currentState.phone,
                        authMethod = AuthMethod.PHONE
                    )
                )
            },
            onLoading = { loading -> updateState { it.copy(isLoading = loading) } },
            onError = { _, message ->
                showError(message?.let(UiText::DynamicString) ?: UiText.StringResource(Res.string.auth_error_login_failed))
            },
            onSuccess = ::handleAuthResult
        )
    }

    private fun submitPhoneCode(code: String) {
        updateState { it.copy(otpError = null) }

        launchNetwork(
            request = {
                confirmPhoneUseCase.execute(
                    ConfirmPhoneCommand(
                        phone = currentState.phone,
                        code = code,
                        flow = currentState.flow
                    )
                )
            },
            onLoading = { loading -> updateState { it.copy(isLoading = loading) } },
            onError = { _, message ->
                updateState {
                    it.copy(
                        otpError = message?.let(UiText::DynamicString)
                            ?: UiText.StringResource(Res.string.auth_error_invalid_code)
                    )
                }
            },
            onSuccess = ::handleAuthResult
        )
    }

    private fun resendPhoneCode() {
        if (currentState.phone.isBlank()) return

        launchNetwork(
            request = {
                resendPhoneCodeUseCase.execute(SendCodeToPhoneCommand(currentState.phone))
            },
            onLoading = { loading -> updateState { it.copy(isLoading = loading) } },
            onError = { _, message ->
                showError(message?.let(UiText::DynamicString) ?: UiText.StringResource(Res.string.auth_error_resend_failed))
            },
            onSuccess = { result ->
                if (result is AuthResult.Continue) {
                    updateState {
                        it.copy(
                            isOtpSent = result.isOtpSent,
                            otpTimerKey = it.otpTimerKey + 1
                        )
                    }
                }
            }
        )
    }

    private fun handleAuthResult(result: AuthResult) {
        when (result) {
            is AuthResult.Success -> {
                if (currentState.flow == AuthFlow.LOGIN) {
                    sendEffect(ClientAuthEffect.NavigateToHome(result.role))
                } else {
                    updateState { it.copy(showSuccessSheet = true, isLoading = false) }
                }
            }

            is AuthResult.Continue -> {
                if (result.nextStep == AuthMethod.PHONE) {
                    updateState {
                        it.copy(
                            step = ClientAuthStep.VerifyPhone,
                            isOtpSent = result.isOtpSent,
                            otpError = null,
                            otpTimerKey = it.otpTimerKey + 1
                        )
                    }

                    if (!result.isOtpSent) {
                        resendPhoneCode()
                    }
                }
            }
        }
    }

    private fun handleBack() {
        updateState { state ->
            when (state.step) {
                ClientAuthStep.GetStarted -> state
                ClientAuthStep.RegistrationForm -> state.copy(step = ClientAuthStep.GetStarted)
                ClientAuthStep.LoginForm -> state.copy(step = ClientAuthStep.GetStarted)
                ClientAuthStep.VerifyPhone -> state.copy(
                    step = if (state.flow == AuthFlow.LOGIN) ClientAuthStep.LoginForm else ClientAuthStep.RegistrationForm,
                    otpError = null
                )
            }
        }
    }

    private fun showError(message: UiText) {
        alertController.show(
            title = UiText.StringResource(DesignRes.string.error_alert_title),
            message = message,
            type = NotificationType.ERROR
        )
    }
}

private fun ClientAuthViewState.withoutErrors(): ClientAuthViewState =
    copy(
        firstNameError = null,
        lastNameError = null,
        phoneError = null,
        emailError = null,
        otpError = null
    )
